import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Heart, Users, Eye, Lock, Leaf } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const EthicsPage = () => {
  const principles = [
    {
      icon: <Heart size={28} />,
      title: "Consent as Foundation",
      description: "Every interaction, practice, and exploration is rooted in explicit, ongoing consent. Your boundaries are sacred, and you are always in control of your experience. Consent can be withdrawn at any time without explanation or judgment."
    },
    {
      icon: <Shield size={28} />,
      title: "Trauma-Responsive Practice",
      description: "I approach all work with an understanding that trauma lives in the body. My practices are designed to support regulation, not overwhelm. We move at your pace, honoring what your nervous system can hold in each moment."
    },
    {
      icon: <Lock size={28} />,
      title: "Confidentiality & Privacy",
      description: "What you share in our work together remains confidential. Your story, your healing journey, and your identity are protected. I maintain strict privacy protocols and do not share identifying information without explicit consent."
    },
    {
      icon: <Eye size={28} />,
      title: "Transparency & Honesty",
      description: "I am committed to honest communication about my qualifications, the scope of my practice, and the nature of our work together. I will be clear about what I can and cannot offer, and I will refer you to other practitioners when appropriate."
    },
    {
      icon: <Users size={28} />,
      title: "Cultural Humility",
      description: "While my work centers Black survivors, I approach each person as an individual with unique experiences, identities, and needs. I commit to ongoing learning and acknowledge that healing looks different for everyone."
    },
    {
      icon: <Leaf size={28} />,
      title: "Non-Exploitation",
      description: "I will never exploit the vulnerability inherent in healing work. My role is to support your empowerment, not to create dependency. I maintain clear boundaries and prioritize your wellbeing above all else."
    }
  ];

  const commitments = [
    "I will honor your autonomy and right to make decisions about your own body and healing journey.",
    "I will maintain appropriate boundaries in all professional relationships.",
    "I will continue my own healing work and professional development.",
    "I will acknowledge my limitations and refer to other professionals when needed.",
    "I will not engage in dual relationships that could compromise the integrity of our work.",
    "I will be accountable for my impact, regardless of my intent.",
    "I will create and maintain spaces that are affirming of diverse identities and experiences."
  ];

  return (
    <div data-testid="ethics-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing" data-testid="ethics-hero">
        <div className="section-container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.15 } }
            }}
            className="max-w-3xl"
          >
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4"
            >
              Code of Ethics
            </motion.p>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-heading text-4xl md:text-5xl text-text-primary mb-8 leading-tight"
            >
              Sacred{" "}
              <span className="italic text-brand-accent">Commitments</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-body text-lg text-text-secondary leading-relaxed"
            >
              The work of healing is sacred work. It requires trust, vulnerability, and 
              an unwavering commitment to ethical practice. These principles guide every 
              aspect of my practice and my relationships with those I serve.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Opening Statement */}
      <section className="pb-16" data-testid="ethics-statement">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="card border-l-4 border-l-brand-accent">
              <p className="font-accent text-xl md:text-2xl text-text-primary leading-relaxed mb-6">
                "As a practitioner who works with survivors of trauma, I hold a profound 
                responsibility. The trust placed in me is not taken lightly. I commit to 
                maintaining the highest ethical standards in service of your healing and 
                liberation."
              </p>
              <p className="font-body text-sm text-text-secondary uppercase tracking-wider">
                — Inas-Khalidah Mahdi
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="section-spacing bg-bg-secondary" data-testid="ethics-principles">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-4">
              Core Principles
            </h2>
            <p className="font-body text-text-secondary max-w-2xl">
              These principles form the ethical foundation of all my work with individuals, 
              communities, and institutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {principles.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
                data-testid={`ethics-principle-${index}`}
              >
                <div className="text-brand-accent mb-4">
                  {principle.icon}
                </div>
                <h3 className="font-heading text-xl text-text-primary mb-4">
                  {principle.title}
                </h3>
                <p className="font-body text-text-secondary text-sm leading-relaxed">
                  {principle.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* My Commitments */}
      <section className="section-spacing" data-testid="ethics-commitments">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl text-text-primary mb-8">
                My Commitments to You
              </h2>
              
              <div className="space-y-4">
                {commitments.map((commitment, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-6 h-6 bg-brand-accent/20 text-brand-accent flex items-center justify-center text-sm">
                      ✓
                    </div>
                    <p className="font-body text-text-secondary leading-relaxed">
                      {commitment}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="font-heading text-3xl text-text-primary mb-8">
                Your Rights
              </h2>

              <div className="card bg-brand-deep-green/30 border-brand-deep-green">
                <div className="space-y-6 font-body text-text-secondary">
                  <p>
                    <span className="text-text-primary font-medium">You have the right</span> to 
                    ask questions about my training, experience, and approach at any time.
                  </p>
                  <p>
                    <span className="text-text-primary font-medium">You have the right</span> to 
                    decline any practice or suggestion that doesn't feel right for you.
                  </p>
                  <p>
                    <span className="text-text-primary font-medium">You have the right</span> to 
                    end our work together at any time, for any reason.
                  </p>
                  <p>
                    <span className="text-text-primary font-medium">You have the right</span> to 
                    provide feedback about your experience working with me.
                  </p>
                  <p>
                    <span className="text-text-primary font-medium">You have the right</span> to 
                    be treated with dignity, respect, and cultural sensitivity.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scope of Practice */}
      <section className="section-spacing bg-bg-secondary" data-testid="ethics-scope">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <h2 className="font-heading text-3xl text-text-primary mb-8">
              Scope of Practice
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="card">
                <h3 className="font-heading text-xl text-text-primary mb-4">
                  What I Offer
                </h3>
                <ul className="space-y-3 font-body text-text-secondary text-sm">
                  <li>• Somatic education and embodiment practices</li>
                  <li>• Trauma-responsive coaching and support</li>
                  <li>• Community healing circles and facilitation</li>
                  <li>• Educational workshops and speaking engagements</li>
                  <li>• Provider training on embodied approaches</li>
                </ul>
              </div>

              <div className="card">
                <h3 className="font-heading text-xl text-text-primary mb-4">
                  What I Do Not Provide
                </h3>
                <ul className="space-y-3 font-body text-text-secondary text-sm">
                  <li>• Psychotherapy or mental health treatment</li>
                  <li>• Medical advice or diagnosis</li>
                  <li>• Crisis intervention services</li>
                  <li>• Legal or financial advice</li>
                </ul>
                <p className="mt-4 text-xs text-text-secondary/70">
                  If you need these services, I can provide referrals to qualified professionals.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing" data-testid="ethics-cta">
        <div className="section-container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl text-text-primary mb-6">
              Questions About My Practice?
            </h2>
            <p className="font-body text-text-secondary mb-8">
              I welcome questions about my ethical commitments, training, or approach. 
              Transparency and open communication are essential to the trust required 
              for this work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/about" data-testid="ethics-cta-about" className="btn-secondary">
                Learn More About Me
              </Link>
              <Link to="/book" data-testid="ethics-cta-book" className="btn-primary">
                Book a Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EthicsPage;
